import {useAxios} from "../axios/useAxios";
import {AxiosResponse} from "axios";

const request = useAxios();

export type ApplicationRegistrationType = {
  email: string;
  password: string;
}

export type ApplicationUpdateType = {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  phone?: string;
  userCategoryId?: number | null;
  biography?: string;
  designation?: string;
  genderId?: number;
  departmentId?: number;
  bloodGroupId?: number;
  maritalStatusId?: number;
  professionalTitle?: string;
  titleId?: number;
  contactAddress?: string;
  spouseName?: string;
  spouseWorkingPlace?: string;
  nin?: string;
  alternativeEmail?: string;
}

export type ApplicationDocumentType = {
  documentName: string;
  documentDescription?: string;
  documentFile: File;
}

// Register new applicant
export const registerApplicantApi = async (data: ApplicationRegistrationType): Promise<AxiosResponse> => {
  return await request.post({
    url: '/api/applications/register',
    headersType: 'application/json',
    data,
  });
}

// Get application by user ID
export const getApplicationByUserIdApi = async (userId: number): Promise<AxiosResponse> => {
  return await request.get({
    url: `/api/applications/user/${userId}`,
  });
}

// Get application by application ID
export const getApplicationByIdApi = async (applicationId: number): Promise<AxiosResponse> => {
  return await request.get({
    url: `/api/applications/${applicationId}`,
  });
}

// Update application information
export const updateApplicationApi = async (applicationId: number, data: ApplicationUpdateType): Promise<AxiosResponse> => {
  return await request.put({
    url: `/api/applications/${applicationId}`,
    headersType: 'application/json',
    data,
  });
}

// Add document to application
export const addApplicationDocumentApi = async (applicationId: number, data: FormData): Promise<AxiosResponse> => {
  return await request.post({
    url: `/api/applications/${applicationId}/documents`,
    headersType: 'multipart/form-data',
    data,
  });
}

// Get application documents
export const getApplicationDocumentsApi = async (applicationId: number): Promise<AxiosResponse> => {
  return await request.get({
    url: `/api/applications/${applicationId}/documents`,
  });
}

// Delete document
export const deleteApplicationDocumentApi = async (applicationId: number, documentId: number): Promise<AxiosResponse> => {
  return await request.delete({
    url: `/api/applications/${applicationId}/documents/${documentId}`,
  });
}

// Download document
export const downloadApplicationDocumentApi = async (documentId: number): Promise<AxiosResponse> => {
  return await request.get({
    url: `/api/applications/documents/${documentId}/download`,
    responseType: 'blob',
  });
}

// Submit application
export const submitApplicationApi = async (applicationId: number): Promise<AxiosResponse> => {
  return await request.post({
    url: `/api/applications/${applicationId}/submit`,
  });
}

// Withdraw application
export const withdrawApplicationApi = async (applicationId: number): Promise<AxiosResponse> => {
  return await request.post({
    url: `/api/applications/${applicationId}/withdraw`,
  });
}

// Check if user has existing application
export const checkUserApplicationApi = async (userId: number): Promise<AxiosResponse> => {
  return await request.get({
    url: `/api/applications/check/${userId}`,
  });
}

// Admin endpoints (for future use)
export const getAllApplicationsApi = async (): Promise<AxiosResponse> => {
  return await request.get({
    url: '/api/applications/',
  });
}

export const getApplicationsByStatusApi = async (status: string): Promise<AxiosResponse> => {
  return await request.get({
    url: `/api/applications/status/${status}`,
  });
}

export const reviewApplicationApi = async (applicationId: number, status: string, reviewNotes?: string): Promise<AxiosResponse> => {
  return await request.post({
    url: `/api/applications/${applicationId}/review`,
    params: { status, reviewNotes },
  });
}

export const getApplicationStatisticsApi = async (): Promise<AxiosResponse> => {
  return await request.get({
    url: '/api/applications/statistics',
  });
}

export const deleteApplicationApi = async (applicationId: number): Promise<AxiosResponse> => {
  return await request.delete({
    url: `/api/applications/${applicationId}`,
  });
}
